/* Unified buy box (Subscribe & Save / One-time).
   Manages the shared option pills → hidden variant id, the mode accordion →
   selling_plan input + submit label, delivery frequency, and the live prices.
   The actual add-to-cart is handled by the theme's <product-form> (product-form.js),
   so cart drawer / spinner / Shop Pay / installments all work unchanged. */
(function () {
  if (customElements.get('subscription-builder')) return;

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  class SubscriptionBuilder extends HTMLElement {
    connectedCallback() {
      var dataEl = this.querySelector('[data-sb-data]');
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        this.data = { variants: [], hasPlans: false };
      }

      this.form = this.closest('form');
      this.variantIdInput = this.form ? this.form.querySelector('.sb__variant-id') : null;
      this.sellingPlanInput = this.form ? this.form.querySelector('.sb__selling-plan') : null;
      this.submitBtn = this.querySelector('.sb__cta');
      this.submitLabel = this.querySelector('[data-sb-submit-label]');

      this.subPriceEl = this.querySelector('[data-sb-sub-price]');
      this.subCompareEl = this.querySelector('[data-sb-sub-compare]');
      this.oneTimePriceEls = this.querySelectorAll('[data-sb-onetime-price]');
      this.badgeEl = this.querySelector('[data-sb-badge]');
      this.planDiscounts = this.data.planDiscounts || {};

      this.subLabel = this.dataset.subLabel || 'Subscribe & Save';
      this.onetimeLabel = this.dataset.onetimeLabel || 'Add to cart';

      this.addEventListener('change', this.onChange.bind(this));

      this.setMode(this.currentMode);
      this.resolveVariant();
      // The server already rendered the correct default price/media, so only
      // start syncing the rest of the page on subsequent user changes.
      this._ready = true;
    }

    get selectedOptions() {
      return Array.prototype.map.call(
        this.querySelectorAll('[data-sb-value]:checked'),
        function (i) { return i.value; }
      );
    }

    get currentMode() {
      var m = this.querySelector('[data-sb-mode]:checked');
      return m ? m.value : 'onetime';
    }

    get selectedPlan() {
      var sel = this.querySelector('select[data-sb-plan]');
      if (sel) return sel.value;
      var p = this.querySelector('[data-sb-plan]:checked');
      return p ? p.value : null;
    }

    get currentVariant() {
      var variants = this.data.variants || [];
      // Single-variant products have no option pills → use the only variant.
      if (!this.querySelectorAll('[data-sb-value]').length) return variants[0];
      var opts = this.selectedOptions;
      return variants.find(function (v) {
        return v.options.length === opts.length && v.options.every(function (o, i) { return o === opts[i]; });
      });
    }

    onChange(e) {
      if (e.target.matches('[data-sb-mode]')) {
        this.setMode(e.target.value);
      } else if (e.target.matches('[data-sb-plan]')) {
        this.syncSellingPlan();
        this.updatePrices();
      } else if (e.target.matches('[data-sb-value]')) {
        this.resolveVariant();
      }
    }

    setMode(mode) {
      var subscribe = mode === 'subscribe';
      this.classList.toggle('is-subscribe', subscribe);
      this.classList.toggle('is-onetime', !subscribe);
      if (this.sellingPlanInput) {
        this.sellingPlanInput.disabled = !subscribe; // excluded from FormData when disabled → one-time
      }
      this.syncSellingPlan();
      this.refreshAvailability();
    }

    syncSellingPlan() {
      if (this.sellingPlanInput && this.currentMode === 'subscribe') {
        var plan = this.selectedPlan;
        if (plan) this.sellingPlanInput.value = plan;
      }
    }

    resolveVariant() {
      var v = this.currentVariant;
      if (this.variantIdInput && v) {
        this.variantIdInput.value = v.id;
        this.variantIdInput.disabled = !v.available;
      }
      this.syncSellingPlan();
      this.updatePrices();
      this.refreshAvailability();
      // Since this buy box replaces the native variant picker, we drive the
      // rest of the product page ourselves: brew guide, image, and price/SKU.
      if (v) {
        document.dispatchEvent(
          new CustomEvent('sb:variantchange', { detail: { variantId: v.id } })
        );
        if (this._ready) this.syncProductPage(v);
      }
    }

    // Keep the rest of the PDP (media gallery + Dawn price/SKU/inventory blocks)
    // coherent with the variant chosen in the buy box. We fetch the section for
    // the selected variant (Section Rendering API) and reuse product-info's own
    // update logic — this is required for media because "hide variants images"
    // means other variants' images aren't in the DOM until merged from the fetch.
    syncProductPage(v) {
      var productInfo = this.closest('product-info');
      if (!productInfo) return;
      var sectionId = productInfo.dataset.section;
      var url = productInfo.dataset.url;
      if (!url) return;

      if (this._syncAbort) this._syncAbort.abort();
      this._syncAbort = new AbortController();
      fetch(url + '?variant=' + encodeURIComponent(v.id) + '&section_id=' + sectionId, {
        signal: this._syncAbort.signal,
      })
        .then(function (r) { return r.text(); })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');

          // Price / SKU / inventory / price-per-item.
          ['price', 'Sku', 'Inventory', 'Price-Per-Item'].forEach(function (id) {
            var source = doc.getElementById(id + '-' + sectionId);
            var dest = productInfo.querySelector('#' + id + '-' + sectionId);
            if (source && dest) {
              dest.innerHTML = source.innerHTML;
              dest.classList.toggle('hidden', source.classList.contains('hidden'));
            }
          });

          // Image — delegate to product-info.updateMedia, which merges the
          // variant's media in from the fetched HTML and activates it.
          if (v.mediaId != null && v.mediaId !== '' && typeof productInfo.updateMedia === 'function') {
            productInfo.updateMedia(doc, v.mediaId);
          }
        })
        .catch(function (e) {
          if (e.name !== 'AbortError') console.error(e);
        });
    }

    updatePrices() {
      var v = this.currentVariant;
      if (!v) return;
      each(this.oneTimePriceEls, function (el) { el.textContent = v.oneTime; });
      var planId = this.selectedPlan;
      var p = v.prices && planId ? v.prices[planId] : null;
      if (p) {
        if (this.subPriceEl) this.subPriceEl.textContent = p.price;
        if (this.subCompareEl) this.subCompareEl.textContent = p.compareAt;
      }
      this.updateBadge();
    }

    updateBadge() {
      if (!this.badgeEl) return;
      var d = this.planDiscounts[this.selectedPlan];
      if (d && d > 0) {
        this.badgeEl.textContent = 'SAVE ' + d + '%';
        this.badgeEl.hidden = false;
      } else {
        this.badgeEl.hidden = true;
      }
    }

    refreshAvailability() {
      if (!this.submitBtn) return;
      var v = this.currentVariant;
      var available = !!(v && v.available);
      this.submitBtn.disabled = !available;
      if (!this.submitLabel) return;
      if (!available) {
        this.submitLabel.textContent = v ? 'Sold out' : 'Unavailable';
      } else {
        this.submitLabel.textContent = this.currentMode === 'subscribe' ? this.subLabel : this.onetimeLabel;
      }
    }
  }

  customElements.define('subscription-builder', SubscriptionBuilder);
})();
